<script lang="ts">
  import {
    getLLMConfig,
    updateLLMConfig,
    testLLMConnection,
    getUncategorizedTransactions,
    categorizeWithLLM,
    createRule,
    type LLMProvider,
    type LLMConfigResponse
  } from '../../lib/api';
  import { upsertTransaction, getTransactions, getCategories, getRules } from '../../lib/storage';
  import { levenshteinDistance, calculateSimilarity } from '../../lib/utils/string-similarity';
  import { Button, FormCheckbox } from '../common';
  import BulkCategorizationPanel from './BulkCategorizationPanel.svelte';
  import BulkReviewModal from './BulkReviewModal.svelte';
  import ProviderConfigSection from './ProviderConfigSection.svelte';
  import CategorizationWizardModal from './CategorizationWizardModal.svelte';

  let { onUpdate = () => {} }: { onUpdate: () => void } = $props();

  // State
  let config = $state<LLMConfigResponse | null>(null);
  let loading = $state(false);
  let testing = $state(false);
  let saving = $state(false);
  let error = $state<string | null>(null);
  let success = $state<string | null>(null);
  let testResult = $state<{ success: boolean; message?: string; error?: string } | null>(null);

  // Form state
  let selectedProvider = $state<LLMProvider>('ollama');
  let apiKey = $state('');
  let model = $state('');
  let enabled = $state(false);
  let optedIntoCloud = $state(false);
  let ollamaUrl = $state('http://localhost:11434');
  let showApiKey = $state(false);

  // Categorization wizard state
  let showWizard = $state(false);
  let loadingTransactions = $state(false);
  let uncategorizedGroups = $state<Array<{ description: string; count: number; transactionIds: string[] }>>([]);
  let currentGroupIndex = $state(0);
  let categorizingCurrent = $state(false);
  let currentSuggestion = $state<{ category: string; confidence: number; reasoning: string; displayName: string } | null>(null);
  let createRuleForCurrent = $state(true);
  let categories = $state<string[]>([]);

  // Bulk categorization state
  let uncategorizedTransactions = $state<any[]>([]);
  let bulkProcessing = $state(false);
  let bulkProgress = $state({ current: 0, total: 0 });
  let showReviewModal = $state(false);
  let bulkSuggestions = $state<any[]>([]);

  // Load config on mount
  $effect(() => {
    loadConfig();
  });

  async function loadConfig() {
    try {
      loading = true;
      error = null;
      config = await getLLMConfig();

      // Update form state
      selectedProvider = config.provider;
      enabled = config.enabled;
      optedIntoCloud = config.optedIntoCloud;
      model = config.model || '';
      ollamaUrl = config.settings.ollamaUrl;

      // Don't populate API key field (security)
      apiKey = '';
    } catch (err) {
      error = err instanceof Error ? err.message : 'Failed to load configuration';
      console.error('Error loading LLM config:', err);
    } finally {
      loading = false;
    }
  }

  async function handleSave() {
    try {
      saving = true;
      error = null;
      success = null;
      testResult = null;

      // Validate cloud provider opt-in
      if ((selectedProvider === 'anthropic' || selectedProvider === 'openai') && !optedIntoCloud) {
        error = 'You must opt in to cloud data processing to use cloud AI providers';
        return;
      }

      // Validate API key for cloud providers
      if ((selectedProvider === 'anthropic' || selectedProvider === 'openai') && !apiKey && !config?.apiKeyConfigured) {
        error = 'API key is required for cloud providers';
        return;
      }

      const updates: any = {
        provider: selectedProvider,
        enabled,
        optedIntoCloud,
        model: model || null,
        settings: {
          ollamaUrl
        }
      };

      // Only include API key if it was changed
      if (apiKey) {
        updates.apiKey = apiKey;
      }

      config = await updateLLMConfig(updates);
      success = 'Configuration saved successfully';
      onUpdate();

      // Clear the API key field after saving
      apiKey = '';
    } catch (err) {
      error = err instanceof Error ? err.message : 'Failed to save configuration';
      console.error('Error saving LLM config:', err);
    } finally {
      saving = false;
    }
  }

  async function handleTestConnection() {
    try {
      testing = true;
      error = null;
      success = null;
      testResult = null;

      const result = await testLLMConnection();
      testResult = result;

      if (result.success) {
        success = `Connection successful! Provider: ${result.provider}, Model: ${result.model}`;
      } else {
        error = result.error || 'Connection test failed';
      }
    } catch (err) {
      error = err instanceof Error ? err.message : 'Connection test failed';
      testResult = { success: false, error: error };
      console.error('Error testing connection:', err);
    } finally {
      testing = false;
    }
  }

  function handleProviderChange(provider: LLMProvider) {
    selectedProvider = provider;
    testResult = null;

    // Set default models
    if (provider === 'ollama') {
      model = model || 'llama3';
      optedIntoCloud = false;
    } else if (provider === 'anthropic') {
      model = model || 'claude-3-haiku-20240307';
    } else if (provider === 'openai') {
      model = model || 'gpt-3.5-turbo';
    }
  }


  // Wizard functions
  async function startWizard() {
    try {
      loadingTransactions = true;
      error = null;
      currentSuggestion = null;

      // Load categories and transactions in parallel
      const [loadedCategories, data] = await Promise.all([
        getCategories(),
        getUncategorizedTransactions()
      ]);

      categories = loadedCategories;
      const transactions = data.transactions || [];

      // Group by fuzzy matching similar descriptions
      const groups: Array<{ description: string; transactionIds: string[] }> = [];
      const processed = new Set<string>();

      // Sort transactions by description to help with grouping
      const sortedTransactions = [...transactions].sort((a: any, b: any) =>
        a.description.localeCompare(b.description)
      );

      // For each transaction, find or create a group
      for (const t of sortedTransactions) {
        if (processed.has(t.id)) continue;

        // Find all unprocessed transactions
        const unprocessed = sortedTransactions.filter((tx: any) => !processed.has(tx.id));

        // Use a simple Levenshtein-like approach for better similarity matching
        // Fuse.js is better for search, but for grouping we want precise similarity
        const similarTransactions = [t];

        for (const other of unprocessed) {
          if (other.id === t.id) continue;

          // Calculate similarity using Levenshtein distance
          const desc1 = t.description;
          const desc2 = other.description;
          const similarity = calculateSimilarity(desc1, desc2);

          console.log(`[Fuzzy Match] Comparing "${desc1}" vs "${desc2}"`);
          console.log(`[Fuzzy Match] Similarity: ${(similarity * 100).toFixed(1)}%`);

          // Group if 85% or more similar
          if (similarity >= 0.85) {
            console.log(`[Fuzzy Match] ✓ Match (${(similarity * 100).toFixed(1)}% similar)`);
            similarTransactions.push(other);
          }
        }

        console.log(`[Fuzzy Match] Group size: ${similarTransactions.length}`);

        // Use the most common or first description as the group name
        const groupDescription = similarTransactions[0].description;
        const transactionIds = similarTransactions.map((tx: any) => tx.id);

        // Mark all as processed
        transactionIds.forEach(id => processed.add(id));

        groups.push({
          description: groupDescription,
          transactionIds
        });
      }

      // Convert to array with counts and sort by count (most common first)
      uncategorizedGroups = groups
        .map(group => ({
          description: group.description,
          count: group.transactionIds.length,
          transactionIds: group.transactionIds
        }))
        .sort((a, b) => b.count - a.count);

      console.log(`[LLM Wizard] Grouped ${transactions.length} transactions into ${uncategorizedGroups.length} groups`);

      if (uncategorizedGroups.length === 0) {
        success = 'No uncategorized transactions found!';
        return;
      }

      currentGroupIndex = 0;
      showWizard = true;
    } catch (err) {
      error = err instanceof Error ? err.message : 'Failed to load transactions';
    } finally {
      loadingTransactions = false;
    }
  }

  async function categorizeCurrent() {
    if (!uncategorizedGroups[currentGroupIndex]) return;

    try {
      categorizingCurrent = true;
      error = null;

      const group = uncategorizedGroups[currentGroupIndex];
      const suggestion = await categorizeWithLLM(group.transactionIds[0]);

      currentSuggestion = {
        category: suggestion.category,
        confidence: suggestion.confidence,
        reasoning: suggestion.reasoning || '',
        displayName: suggestion.displayName || group.description
      };
    } catch (err) {
      error = err instanceof Error ? err.message : 'Failed to categorize';
    } finally {
      categorizingCurrent = false;
    }
  }

  async function acceptSuggestion() {
    if (!currentSuggestion || !uncategorizedGroups[currentGroupIndex]) return;

    try {
      const group = uncategorizedGroups[currentGroupIndex];

      // Load all transactions to update them
      const allTransactions = await getTransactions();

      // Update all transactions in this group
      for (const txId of group.transactionIds) {
        const tx = allTransactions.find(t => t.id === txId);
        if (tx) {
          await upsertTransaction({
            ...tx,
            category: currentSuggestion.category,
            categorySource: 'llm'
          });
        }
      }

      // Create rule if requested
      if (createRuleForCurrent) {
        await createRule({
          pattern: group.description,
          category: currentSuggestion.category,
          normalizedName: currentSuggestion.displayName.trim() || group.description
        });
      }

      success = `Categorized ${group.count} transaction(s) as "${currentSuggestion.category}"`;
      onUpdate();

      // Move to next
      moveNext();
    } catch (err) {
      error = err instanceof Error ? err.message : 'Failed to apply categorization';
    }
  }

  function skip() {
    moveNext();
  }

  function moveNext() {
    currentSuggestion = null;
    createRuleForCurrent = true;

    if (currentGroupIndex < uncategorizedGroups.length - 1) {
      currentGroupIndex++;
    } else {
      // Done
      showWizard = false;
      success = 'Finished categorizing transactions!';
    }
  }

  function closeWizard() {
    showWizard = false;
    currentSuggestion = null;
    uncategorizedGroups = [];
    currentGroupIndex = 0;
  }

  // Bulk categorization functions
  async function loadUncategorizedTransactions() {
    try {
      loadingTransactions = true;

      // Load ALL transactions (not just uncategorized)
      // The filtering will be done in the BulkCategorizationPanel component
      const allTransactions = await getTransactions();

      // Load rules to filter out transactions that already have matching rules
      const rules = await getRules();
      const enabledRules = rules.filter(r => r.enabled);

      // Filter out transactions that would match existing rules
      uncategorizedTransactions = allTransactions.filter(transaction => {
        const lowerDesc = transaction.description.toLowerCase();

        // Check if any enabled rule matches this transaction
        const hasMatchingRule = enabledRules.some(rule => {
          const pattern = rule.pattern.toLowerCase();
          return lowerDesc.includes(pattern);
        });

        // Only include if no matching rule exists
        return !hasMatchingRule;
      });

      // Load categories
      const cats = await getCategories();
      categories = cats;
    } catch (err) {
      error = err instanceof Error ? err.message : 'Failed to load transactions';
      console.error('Error loading transactions:', err);
    } finally {
      loadingTransactions = false;
    }
  }

  async function handleBulkCategorize(event: CustomEvent<{ transactionIds: string[] }>) {
    console.log('[LLMSettingsTab] handleBulkCategorize called', event);
    const { transactionIds } = event.detail;
    console.log('[LLMSettingsTab] Transaction IDs:', transactionIds);

    try {
      bulkProcessing = true;
      bulkProgress = { current: 0, total: transactionIds.length };
      error = null;

      // Call batch categorization endpoint
      const response = await fetch('/api/llm/categorize-batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transactionIds })
      });

      if (!response.ok) {
        throw new Error('Batch categorization failed');
      }

      const data = await response.json();
      bulkSuggestions = data.suggestions || [];
      console.log('[LLMSettingsTab] Bulk suggestions received:', bulkSuggestions);

      // Show review modal
      showReviewModal = true;
    } catch (err) {
      error = err instanceof Error ? err.message : 'Bulk categorization failed';
      console.error('Error in bulk categorization:', err);
    } finally {
      bulkProcessing = false;
      bulkProgress = { current: 0, total: 0 };
    }
  }

  async function handleApplySuggestions(event: CustomEvent<Array<{
    transactionId: string;
    category: string;
    displayName: string;
    createRule: boolean;
  }>>) {
    console.log('[LLMSettingsTab] handleApplySuggestions called', event);
    const updates = event.detail;
    console.log('[LLMSettingsTab] Updates to apply:', updates);

    try {
      // Apply each update
      for (const update of updates) {
        // Extract all values immediately to avoid proxy issues
        const transactionId = String(update.transactionId);
        const category = String(update.category);
        const displayName = String(update.displayName);
        const shouldCreateRule = Boolean(update.createRule);

        console.log('[LLMSettingsTab] Processing update:', { transactionId, category, displayName, createRule: shouldCreateRule });

        const transaction = uncategorizedTransactions.find(t => t.id === transactionId);

        if (!transaction) {
          console.warn('[LLMSettingsTab] Transaction not found, skipping:', transactionId);
          continue;
        }

        // Extract description before any async operations
        const transactionDescription = String(transaction.description);

        console.log('[LLMSettingsTab] Updating transaction:', transactionId);

        // Update transaction with extracted values - upsertTransaction expects full transaction object
        await upsertTransaction({
          ...transaction,
          category: category,
          displayName: displayName,
          categorySource: 'llm'
        });

        // Create rule if requested
        if (shouldCreateRule) {
          try {
            await createRule({
              pattern: transactionDescription,
              category: category,
              normalizedName: displayName || transactionDescription,
              markAsRecurring: false
            });
          } catch (ruleError) {
            // If rule already exists, that's OK - just continue
            console.log('[LLMSettingsTab] Rule already exists or error creating rule:', ruleError);
          }
        }
      }

      // Reload uncategorized transactions
      await loadUncategorizedTransactions();

      success = `Successfully categorized ${updates.length} transaction(s)`;
      showReviewModal = false;
      bulkSuggestions = []; // Clear the suggestions after applying
      onUpdate();
    } catch (err) {
      error = err instanceof Error ? err.message : 'Failed to apply suggestions';
      console.error('Error applying suggestions:', err);
    }
  }

  // Load uncategorized transactions when component mounts and enabled
  $effect(() => {
    if (enabled && !showWizard) {
      loadUncategorizedTransactions();
    }
  });

  const isCloudProvider = $derived(selectedProvider === 'anthropic' || selectedProvider === 'openai');
  const currentGroup = $derived(uncategorizedGroups[currentGroupIndex]);
</script>

<div class="llm-settings">
  <div class="section header-section">
    <h2>AI Categorization Settings</h2>
    <p class="description">
      Configure AI-powered transaction categorization using language models.
      Choose between local (privacy-focused) or cloud-based AI providers.
    </p>
      <p class="description">
        Currently disabled in staging / prod environments
    </p>
  </div>

  {#if loading}
    <div class="loading">Loading configuration...</div>
  {:else if config}
    <!-- Enable/Disable Toggle -->
    <div class="section toggle-section">
      <FormCheckbox
        label="Enable AI Categorization"
        bind:checked={enabled}
      />
    </div>

    <!-- AI Categorization Wizard - Show at top when enabled -->
    {#if enabled && !showWizard}
      <div class="section wizard-section">
        <h3>Categorize Uncategorized Transactions</h3>
        <p class="description">
          Use AI to categorize your uncategorized transactions one by one.
          We'll show you the most common transactions first.
        </p>
        <Button
          variant="primary"
          onclick={startWizard}
          disabled={loadingTransactions}
        >
          {loadingTransactions ? 'Loading...' : 'Start Categorizing'}
        </Button>
      </div>

      <!-- Bulk Categorization Panel -->
      <BulkCategorizationPanel
        bind:uncategorizedTransactions={uncategorizedTransactions}
        bind:isProcessing={bulkProcessing}
        bind:processingProgress={bulkProgress}
        oncategorize={handleBulkCategorize}
      />
    {/if}

    <!-- Settings Grid -->
    <ProviderConfigSection
      {selectedProvider}
      {apiKey}
      {model}
      {ollamaUrl}
      {optedIntoCloud}
      {showApiKey}
      {enabled}
      {config}
      {testing}
      {testResult}
      onProviderChange={handleProviderChange}
      onTestConnection={handleTestConnection}
      onApiKeyChange={(value) => apiKey = value}
      onModelChange={(value) => model = value}
      onOllamaUrlChange={(value) => ollamaUrl = value}
      onOptedIntoCloudChange={(value) => optedIntoCloud = value}
      onShowApiKeyToggle={() => showApiKey = !showApiKey}
    />

    <!-- Categorization Wizard Modal -->
    {#if showWizard && currentGroup}
      <CategorizationWizardModal
        isOpen={true}
        {currentGroup}
        {currentGroupIndex}
        totalGroups={uncategorizedGroups.length}
        {currentSuggestion}
        {categorizingCurrent}
        {createRuleForCurrent}
        {categories}
        onClose={closeWizard}
        onCategorize={categorizeCurrent}
        onAccept={acceptSuggestion}
        onSkip={skip}
        onSuggestionChange={(field, value) => {
          if (currentSuggestion) {
            currentSuggestion[field] = value;
          }
        }}
        onCreateRuleChange={(value) => createRuleForCurrent = value}
      />
    {/if}

    <!-- Usage Statistics -->
    {#if config.usage.totalRequests > 0}
      <div class="section">
        <h3>Usage Statistics</h3>
        <div class="usage-stats">
          <div class="stat">
            <div class="stat-label">Total Categorizations</div>
            <div class="stat-value">{config.usage.totalRequests}</div>
          </div>
          {#if config.usage.lastUsed}
            <div class="stat">
              <div class="stat-label">Last Used</div>
              <div class="stat-value">{new Date(config.usage.lastUsed).toLocaleString()}</div>
            </div>
          {/if}
        </div>
      </div>
    {/if}

    <!-- Error/Success Messages -->
    {#if error}
      <div class="message error">{error}</div>
    {/if}

    {#if success}
      <div class="message success">{success}</div>
    {/if}

    <!-- Save Button -->
    <div class="actions">
      <Button
        variant="primary"
        onclick={handleSave}
        disabled={saving || loading}
      >
        {saving ? 'Saving...' : 'Save Configuration'}
      </Button>
    </div>
  {/if}
</div>

<!-- Bulk Review Modal -->
{#if showReviewModal}
  <BulkReviewModal
    suggestions={bulkSuggestions}
    categories={categories}
    onClose={() => showReviewModal = false}
    onapply={handleApplySuggestions}
  />
{/if}

<style lang="scss">
  .llm-settings {
    max-width: 1200px;
  }

  .section {
    margin-bottom: var(--spacing-2xl);

    h2 {
      font-size: var(--font-size-2xl);
      font-weight: var(--font-weight-semibold);
      margin-bottom: var(--spacing-sm);
      color: var(--color-text-primary);
    }

    h3 {
      font-size: var(--font-size-lg);
      font-weight: var(--font-weight-semibold);
      margin-bottom: var(--spacing-md);
      color: var(--color-text-primary);
    }

    h4 {
      font-size: var(--font-size-base);
      font-weight: var(--font-weight-semibold);
      margin-bottom: var(--spacing-sm);
      color: var(--color-text-primary);
    }

    .description {
      color: var(--color-text-secondary);
      font-size: var(--font-size-sm);
      line-height: var(--line-height-relaxed);
    }

    &.header-section {
      margin-bottom: var(--spacing-xl);
    }

    &.toggle-section {
      margin-bottom: var(--spacing-xl);
    }

    &.wizard-section {
      padding: var(--spacing-lg);
      background: var(--color-background-secondary);
      border-radius: var(--border-radius-md);
      border: var(--border-width-medium) solid var(--color-primary);
      margin-bottom: var(--spacing-2xl);
    }
  }

  .loading {
    padding: var(--spacing-xl);
    text-align: center;
    color: var(--color-text-secondary);
  }

  .usage-stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: var(--spacing-md);

    .stat {
      padding: var(--spacing-md);
      background: var(--color-background-secondary);
      border-radius: var(--border-radius-md);

      .stat-label {
        font-size: var(--font-size-xs);
        color: var(--color-text-tertiary);
        text-transform: uppercase;
        letter-spacing: 0.05em;
        margin-bottom: var(--spacing-xs);
      }

      .stat-value {
        font-size: var(--font-size-xl);
        font-weight: var(--font-weight-semibold);
        color: var(--color-text-primary);
      }
    }
  }

  .message {
    padding: var(--spacing-md);
    border-radius: var(--border-radius-sm);
    margin-bottom: var(--spacing-md);
    font-size: var(--font-size-sm);

    &.error {
      background: var(--color-error-background, var(--color-danger-light));
      color: var(--color-error, var(--color-danger-hover));
      border: var(--border-width-thin) solid var(--color-error, var(--color-danger-hover));
    }

    &.success {
      background: var(--color-success-background, var(--color-success-light));
      color: var(--color-success, var(--color-success));
      border: var(--border-width-thin) solid var(--color-success, var(--color-success));
    }
  }

  .actions {
    display: flex;
    gap: var(--spacing-md);
    padding-top: var(--spacing-lg);
    border-top: var(--border-width-thin) solid var(--color-border);
  }
</style>
